/**
 * FikoCNCCost - CNC Isleme Maliyet Hesaplama Modulu
 *
 * 3-eksen frezeleme, 5-eksen frezeleme, tornalama, EDM ve taslama
 * surecleri icin detayli maliyet hesaplama.
 *
 * Hesaplama: Malzeme + Makine suresi + Takim asinma + Hazirlik + Tolerans/Yuzey carpanlari
 */

import { FikoMaterialDB, MaterialProperties, ManufacturingProcessType } from './FikoMaterialDB';
import { CostLineItem } from './FikoCostEngine';

export type CNCProcessType = 'cnc_milling_3axis' | 'cnc_milling_5axis' | 'cnc_turning' | 'cnc_edm' | 'cnc_grinding';

export interface CNCCostInput {
    partGeometry: {
        volumeCm3: number;
        surfaceAreaCm2: number;
        boundingBox: { x: number; y: number; z: number };
        complexityScore: number;
        holeCount: number;
        pocketCount: number;
        filletCount: number;
        chamferCount: number;
        threadCount: number;
    };
    material: MaterialProperties;
    batchSize: number;
    machiningProcess: CNCProcessType;
    tightestToleranceMM: number;
    requiredSurfaceRa: number;         // um
    numberOfSetups?: number;           // Aparat sayisi (varsayilan otomatik hesap)
    rawStockType?: 'bar' | 'plate' | 'block' | 'near_net'; // Hammadde tipi
}

export interface CNCCostResult {
    costPerPart: number;
    costPerKg: number;
    breakdown: CostLineItem[];
    machiningTimeMinutes: number;
    materialRemovalPercent: number;
    notes: string[];
    notesTR: string[];
}

export class FikoCNCCost {
    private materialDB: FikoMaterialDB;

    constructor(materialDB: FikoMaterialDB) {
        this.materialDB = materialDB;
    }

    calculate(input: CNCCostInput): CNCCostResult {
        const { material, partGeometry, batchSize, machiningProcess } = input;
        const notes: string[] = [];
        const notesTR: string[] = [];
        const breakdown: CostLineItem[] = [];

        const partWeightKg = partGeometry.volumeCm3 * material.density;

        // 1. Hammadde (bar stock) maliyeti
        const rawStockVolumeCm3 = this.estimateRawStockVolume(partGeometry, input.rawStockType);
        const rawStockWeightKg = rawStockVolumeCm3 * material.density;
        const stockPricePerKg = material.barStockPricePerKg || material.pricePerKg * 1.3;
        const materialCost = rawStockWeightKg * stockPricePerKg;
        const materialRemovalPercent = ((rawStockVolumeCm3 - partGeometry.volumeCm3) / rawStockVolumeCm3) * 100;

        breakdown.push({
            category: 'material',
            description: `${material.name} raw stock (${rawStockWeightKg.toFixed(2)}kg at $${stockPricePerKg.toFixed(2)}/kg)`,
            descriptionTR: `${material.nameTR} hammadde (${rawStockWeightKg.toFixed(2)}kg, $${stockPricePerKg.toFixed(2)}/kg)`,
            quantity: rawStockWeightKg,
            unitCost: stockPricePerKg,
            totalCost: materialCost,
            unit: 'kg'
        });

        // 2. Isleme suresi hesabi
        const machiningTimeMin = this.estimateMachiningTime(partGeometry, material, machiningProcess);

        // 3. Tolerans carpani
        const toleranceMultiplier = this.materialDB.getToleranceCostMultiplier(input.tightestToleranceMM);

        // 4. Yuzey finish carpani
        const surfaceMultiplier = this.materialDB.getSurfaceFinishCostMultiplier(input.requiredSurfaceRa);

        // 5. Efektif isleme suresi (carpanlar dahil)
        const effectiveTime = machiningTimeMin * toleranceMultiplier * surfaceMultiplier;

        // 6. Makine maliyeti
        const machineParams = this.materialDB.getMachineParams(machiningProcess as ManufacturingProcessType);
        const hourlyRate = machineParams?.hourlyRate || 75;
        const machineCost = (effectiveTime / 60) * hourlyRate;

        breakdown.push({
            category: 'machine',
            description: `Machine time (${effectiveTime.toFixed(1)} min at $${hourlyRate}/hr, tol x${toleranceMultiplier.toFixed(1)}, Ra x${surfaceMultiplier.toFixed(1)})`,
            descriptionTR: `Makine suresi (${effectiveTime.toFixed(1)} dk, $${hourlyRate}/saat, tolerans x${toleranceMultiplier.toFixed(1)}, Ra x${surfaceMultiplier.toFixed(1)})`,
            quantity: effectiveTime / 60,
            unitCost: hourlyRate,
            totalCost: machineCost,
            unit: 'saat'
        });

        // 7. Takim degisim maliyeti
        const toolChanges = this.estimateToolChanges(partGeometry, machiningProcess);
        const toolChangeTime = (machineParams?.toolChangeSeconds || 30) / 60; // dakika
        const toolChangeCost = toolChanges * toolChangeTime * (hourlyRate / 60);
        if (toolChanges > 0) {
            breakdown.push({
                category: 'machine',
                description: `${toolChanges} tool changes (${(machineParams?.toolChangeSeconds || 30)}s each)`,
                descriptionTR: `${toolChanges} takim degisimi (${(machineParams?.toolChangeSeconds || 30)}sn)`,
                quantity: toolChanges,
                unitCost: toolChangeTime * (hourlyRate / 60),
                totalCost: toolChangeCost,
                unit: 'adet'
            });
        }

        // 8. Takim asinma maliyeti
        const toolWearCost = this.estimateToolWearCost(partGeometry, material, machiningProcess, effectiveTime);
        breakdown.push({
            category: 'tooling',
            description: 'Tool wear/replacement cost',
            descriptionTR: 'Takim asinma/degistirme maliyeti',
            quantity: 1,
            unitCost: toolWearCost,
            totalCost: toolWearCost,
            unit: 'adet'
        });

        // 9. Programlama maliyeti (ilk parca icin, batch'e amorti)
        const programmingCost = this.estimateProgrammingCost(partGeometry, machiningProcess);
        const amortizedProgramming = programmingCost / batchSize;
        breakdown.push({
            category: 'setup',
            description: `CAM programming ($${programmingCost.toFixed(0)} / ${batchSize} parts)`,
            descriptionTR: `CAM programlama ($${programmingCost.toFixed(0)} / ${batchSize} parca)`,
            quantity: 1,
            unitCost: amortizedProgramming,
            totalCost: amortizedProgramming,
            unit: 'adet'
        });

        // 10. Setup/Aparat maliyeti
        const numberOfSetups = input.numberOfSetups || this.estimateSetupCount(partGeometry, machiningProcess);
        const setupTimeMin = (machineParams?.setupTimeMinutes || 45) * numberOfSetups;
        const setupCostTotal = (setupTimeMin / 60) * hourlyRate / batchSize;
        breakdown.push({
            category: 'setup',
            description: `${numberOfSetups} setup(s), ${setupTimeMin} min total (amortized over ${batchSize} parts)`,
            descriptionTR: `${numberOfSetups} aparat, toplam ${setupTimeMin} dk (${batchSize} parcaya amorti)`,
            quantity: 1,
            unitCost: setupCostTotal,
            totalCost: setupCostTotal,
            unit: 'adet'
        });

        // 11. Iscilik (operator)
        const laborRate = 22; // $/hr
        const laborTime = effectiveTime * 0.3; // Operator supervises ~30% of machine time
        const laborCost = (laborTime / 60) * laborRate;
        breakdown.push({
            category: 'labor',
            description: `Operator labor (${laborTime.toFixed(1)} min at $${laborRate}/hr)`,
            descriptionTR: `Operator isciligi (${laborTime.toFixed(1)} dk, $${laborRate}/saat)`,
            quantity: laborTime / 60,
            unitCost: laborRate,
            totalCost: laborCost,
            unit: 'saat'
        });

        // 12. Dis acma (thread) maliyeti
        if (partGeometry.threadCount > 0) {
            const threadCost = partGeometry.threadCount * 2.5; // ~$2.50 per thread
            breakdown.push({
                category: 'machine',
                description: `Threading: ${partGeometry.threadCount} thread(s)`,
                descriptionTR: `Dis acma: ${partGeometry.threadCount} dis`,
                quantity: partGeometry.threadCount,
                unitCost: 2.5,
                totalCost: threadCost,
                unit: 'adet'
            });
        }

        // Toplam
        const costPerPart = breakdown.reduce((sum, item) => sum + item.totalCost, 0);
        const costPerKg = partWeightKg > 0 ? costPerPart / partWeightKg : 0;

        // Notlar
        notes.push(`Material removal: ${materialRemovalPercent.toFixed(0)}% (${(rawStockVolumeCm3 - partGeometry.volumeCm3).toFixed(1)} cm3 removed)`);
        notes.push(`Effective machining time: ${effectiveTime.toFixed(1)} min (base ${machiningTimeMin.toFixed(1)} min)`);
        notesTR.push(`Malzeme kaldirma: %${materialRemovalPercent.toFixed(0)} (${(rawStockVolumeCm3 - partGeometry.volumeCm3).toFixed(1)} cm3 kaldirildi)`);
        notesTR.push(`Efektif isleme suresi: ${effectiveTime.toFixed(1)} dk (baz ${machiningTimeMin.toFixed(1)} dk)`);

        if (machiningProcess === 'cnc_milling_3axis' && partGeometry.complexityScore > 70) {
            notes.push('Consider 5-axis for complex geometry - may reduce setups');
            notesTR.push('Karmasik geometri icin 5-eksen dusunun - aparat sayisini azaltabilir');
        }

        return {
            costPerPart: Math.round(costPerPart * 100) / 100,
            costPerKg: Math.round(costPerKg * 100) / 100,
            breakdown,
            machiningTimeMinutes: effectiveTime,
            materialRemovalPercent,
            notes,
            notesTR,
        };
    }

    // ============================================================
    // HELPER METHODS
    // ============================================================

    private estimateRawStockVolume(geom: any, stockType?: string): number {
        const bb = geom.boundingBox;
        // Add 5mm margin on each side for clamping
        const margin = stockType === 'near_net' ? 3 : 5;
        const stockX = bb.x + margin * 2;
        const stockY = bb.y + margin * 2;
        const stockZ = bb.z + margin * 2;
        return (stockX * stockY * stockZ) / 1000; // mm3 to cm3
    }

    private estimateMachiningTime(geom: any, material: MaterialProperties, process: CNCProcessType): number {
        const removalVolumeCm3 = this.estimateRawStockVolume(geom) - geom.volumeCm3;

        // Material removal rate (cm3/min) based on material machinability
        const baseMRR: Record<CNCProcessType, number> = {
            cnc_milling_3axis: 15,
            cnc_milling_5axis: 12,
            cnc_turning: 25,
            cnc_edm: 0.5,      // EDM is very slow
            cnc_grinding: 2,    // Grinding removes little material
        };

        const mrr = (baseMRR[process] || 10) * (material.machinabilityIndex / 100);
        const roughingTime = removalVolumeCm3 / Math.max(0.1, mrr);

        // Finishing passes: ~30% of roughing time for milling, more for grinding
        const finishingFactor: Record<CNCProcessType, number> = {
            cnc_milling_3axis: 0.35,
            cnc_milling_5axis: 0.40,
            cnc_turning: 0.25,
            cnc_edm: 0.50,
            cnc_grinding: 1.5,
        };

        const finishingTime = roughingTime * (finishingFactor[process] || 0.3);

        // Feature-based time additions
        const featureTime =
            (geom.holeCount || 0) * 1.5 +         // 1.5 min per hole
            (geom.pocketCount || 0) * 3.0 +        // 3 min per pocket
            (geom.filletCount || 0) * 0.5 +        // 0.5 min per fillet
            (geom.chamferCount || 0) * 0.3;         // 0.3 min per chamfer

        // Complexity factor
        const complexityMultiplier = 1 + (geom.complexityScore / 100) * 0.5;

        return (roughingTime + finishingTime + featureTime) * complexityMultiplier;
    }

    private estimateToolChanges(geom: any, process: CNCProcessType): number {
        if (process === 'cnc_edm' || process === 'cnc_grinding') return 1;

        // Base tools: roughing, finishing, drilling
        let tools = 2; // Roughing + finishing
        if (geom.holeCount > 0) tools += Math.min(geom.holeCount, 5); // Different drill sizes
        if (geom.pocketCount > 0) tools += 1;
        if (geom.chamferCount > 0) tools += 1;
        if (geom.threadCount > 0) tools += 1;
        if (geom.filletCount > 0 && process !== 'cnc_turning') tools += 1; // Ball end mill

        return tools;
    }

    private estimateToolWearCost(geom: any, material: MaterialProperties, process: CNCProcessType, machiningTimeMin: number): number {
        // Tool life varies by material hardness
        const toolLifeMinutes = 120 * (material.machinabilityIndex / 100); // Base 120 min for easy materials
        const toolCost = process === 'cnc_edm' ? 15 : process === 'cnc_grinding' ? 25 : 8; // Average tool/insert cost
        const toolsUsed = machiningTimeMin / Math.max(10, toolLifeMinutes);
        return toolsUsed * toolCost;
    }

    private estimateProgrammingCost(geom: any, process: CNCProcessType): number {
        const baseCost: Record<CNCProcessType, number> = {
            cnc_milling_3axis: 80,
            cnc_milling_5axis: 150,
            cnc_turning: 50,
            cnc_edm: 120,
            cnc_grinding: 40,
        };
        const complexityFactor = 1 + (geom.complexityScore / 100) * 2;
        return (baseCost[process] || 80) * complexityFactor;
    }

    private estimateSetupCount(geom: any, process: CNCProcessType): number {
        if (process === 'cnc_turning') return 1; // Usually single setup
        if (process === 'cnc_milling_5axis') return 1; // 5-axis handles most in one setup
        if (process === 'cnc_edm') return 1;
        if (process === 'cnc_grinding') return 1;

        // 3-axis milling: number of sides needing machining
        const complexity = geom.complexityScore || 50;
        if (complexity < 30) return 1;
        if (complexity < 60) return 2;
        if (complexity < 80) return 3;
        return 4; // Very complex = 4+ setups
    }
}
